import type { ReactNode } from "react";
import { PlusCircle } from "@phosphor-icons/react";

import type { BillingSummaryResponse } from "../api/billingApi";
import { defaultIconProps } from "../../shared/ui/icons";

const walletCardClasses =
  "relative flex w-full max-w-full flex-col justify-between overflow-hidden rounded-xl bg-gradient-to-br from-sky-500 via-indigo-600 to-slate-900 p-4 text-white shadow-xl ring-1 ring-sky-200/40 sm:w-auto sm:max-w-sm sm:p-5 dark:from-sky-700 dark:via-indigo-700 dark:to-slate-950 dark:ring-sky-900/50";
const walletBackdropClasses =
  "pointer-events-none absolute inset-0 opacity-30 blur-3xl";
const balanceValueClasses = "text-4xl font-semibold tracking-tight text-white sm:text-5xl";
const balanceLabelClasses = "text-sm uppercase tracking-wide text-white/70";
const metaStackClasses =
  "mt-3 flex flex-wrap items-center gap-3 text-sm text-white/80";
const brandChipClasses =
  "inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80 backdrop-blur ring-1 ring-white/15";
const avatarFallbackClasses =
  "grid h-9 w-9 place-items-center rounded-2xl bg-white/15 text-white shadow-inner";
const badgeBaseClasses =
  "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide";
const actionButtonClasses =
  "pm-btn pm-btn-primary pm-btn-sm !bg-white !text-slate-900 !ring-0 !border-transparent hover:!bg-white focus:!bg-white";

interface WalletCardProps {
  summary: BillingSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
  userName?: string;
  onClick?: () => void;
  footer?: ReactNode;
}

export function WalletCard({
  summary,
  isLoading,
  error,
  userName,
  onClick,
  footer,
}: WalletCardProps) {
  const balanceCredits =
    summary?.balance_credits != null ? summary.balance_credits : 0;
  const walletSuffix =
    summary?.wallet_id && summary.wallet_id.length >= 6
      ? summary.wallet_id.slice(-6)
      : "———";
  const hasPositiveBalance = balanceCredits > 0;
  const wrapperClasses = [
    "w-full sm:w-auto sm:max-w-sm",
    hasPositiveBalance
      ? "pm-aurora-border rounded-2xl p-[3px] shadow-inner shadow-sky-200/40 dark:shadow-sky-900/40"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col gap-3">
      <div className={wrapperClasses}>
        <div
          className={`${walletCardClasses} ${onClick ? "cursor-pointer" : ""}`}
          onClick={onClick}
          role={onClick ? "button" : undefined}
          tabIndex={onClick ? 0 : -1}
        >
          <div className={walletBackdropClasses} aria-hidden />
          {hasPositiveBalance ? (
            <div className="pm-card-glow pointer-events-none absolute inset-1 rounded-lg mix-blend-screen" aria-hidden />
          ) : null}
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-white/70">
                My Digital Wallet
              </span>
              <span className="text-sm text-white/80">
                Enjoy the full power of LotLocator.ph!
              </span>
            </div>
            <div className={brandChipClasses} aria-label="LotLocator">
              <span className={avatarFallbackClasses} aria-hidden>
                <img
                  src="/lotlocator-icon.png"
                  alt=""
                  className="h-7 w-7 rounded-xl ring-1 ring-white/20"
                  loading="lazy"
                />
              </span>
              <span>LotLocator</span>
            </div>
          </div>

          <div className="mt-6 flex items-end justify-between gap-4">
            <div>
              {isLoading ? (
                <p className="text-sm text-white/80">Loading…</p>
              ) : error ? (
                <p className="text-sm text-rose-100">Couldn&apos;t load balance.</p>
              ) : (
                <>
                  <div className="flex items-baseline gap-3">
                    <span className={balanceValueClasses}>
                      {balanceCredits.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                    <span className={balanceLabelClasses}>credits</span>
                  </div>
                  <div className={metaStackClasses}>
                    <StatusBadge tone={summary?.status === "active" ? "success" : "danger"}>
                      {summary?.status === "active" ? "Active" : "Suspended"}
                    </StatusBadge>
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 text-right text-white/80">
              {userName ? (
                <span className="text-sm font-semibold tracking-wide">
                  {userName}
                </span>
              ) : null}
              <span className="text-xs uppercase tracking-widest">
                Wallet •••• {walletSuffix}
              </span>
            </div>
          </div>
        </div>
      </div>
      {onClick ? (
        <div className="mt-4 flex flex-col gap-2 text-white/85 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide">
              Pay with
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {paymentProviders.map((provider) => (
                <span
                  key={provider.label}
                  className={`flex h-6 min-w-[36px] items-center justify-center rounded-sm px-2 text-[10px] font-semibold uppercase shadow-sm ring-1 ring-white/40 ${provider.bgClass}`}
                  aria-label={provider.label}
                >
                  {provider.text}
                </span>
              ))}
            </div>
          </div>
          <button type="button" className={actionButtonClasses} onClick={onClick}>
            <PlusCircle {...defaultIconProps} aria-hidden />
            Top up
          </button>
        </div>
      ) : null}
      {footer}
    </div>
  );
}

function StatusBadge({
  tone,
  children,
}: {
  tone: "success" | "danger";
  children: ReactNode;
}) {
  const toneClasses =
    tone === "success"
      ? "bg-white/15 text-emerald-50 ring-1 ring-emerald-200/60 dark:text-emerald-50"
      : "bg-white/15 text-rose-50 ring-1 ring-rose-200/60 dark:text-rose-50";

  return (
    <span
      className={`${badgeBaseClasses} ${toneClasses}`}
    >
      {children}
    </span>
  );
}

const paymentProviders: Array<{ label: string; text: ReactNode; bgClass: string }> = [
  {
    label: "Visa",
    text: "VISA",
    bgClass: "bg-[#1A1F71]",
  },
  {
    label: "Mastercard",
    text: "MC",
    bgClass: "bg-gradient-to-r from-[#EB001B] to-[#F79E1B]",
  },
  {
    label: "PayPal",
    text: "PP",
    bgClass: "bg-[#003087]",
  },
  {
    label: "GCash",
    text: "GCash",
    bgClass: "bg-[#0275d8]",
  },
];
